/* eslint-disable simple-import-sort/exports */
import axios, { AxiosError } from 'axios'
import { History } from 'history'
import i18n from 'i18next'
import { Dispatch } from 'react'
import { toast } from 'react-toastify'

import { ObservationsResult } from '../types/observation'
import { del, get, post, update } from '../utils/api'
import { handleApiErrors } from '../utils/apiErrors'
import { locations } from '../utils/locations'
import { getQueryString } from '../utils/queryString'
import type {
  Product,
  ProductClasses,
  Products,
  Project,
  Projects,
  Training,
  TrainingDownload,
  Trainings,
  TrainingTest,
  TrainingTests,
} from './store.types'
import { ProductType } from './store.types'
import {
  Actions,
  AlertNotification,
  RunStatus,
  Types,
} from './storeReducer.types'

const endpoints = {
  getProjects: (
    limit: number,
    offset: number,
    ordering?: string,
    filterParams?: string
  ) =>
    `/projects/?limit=${limit}&offset=${offset}${
      ordering ? `&ordering=${ordering}` : ''
    }${filterParams || ''}`,
  createProject: '/projects/',
  updateProject: (id: number) => `/projects/${id}/`,
  getProject: (id: number) => `/projects/${id}/`,
  getProducts: (
    projectId: number,
    limit: number,
    offset: number,
    ordering?: string,
    filterParams?: string
  ) =>
    `/products/?project=${projectId}&limit=${limit}&offset=${offset}${
      ordering ? `&ordering=${ordering}` : ''
    }${filterParams || ''}`,
  createProduct: '/products/',
  updateProduct: (id: number) => `/products/${id}/`,
  getProduct: (id: number) => `/products/${id}/`,
  getProductClasses: (productId: number, limit: number, offset: number) =>
    `/product_classes/?product=${productId}&limit=${limit}&offset=${offset}&ordering=id`,
  getTrainings: (
    productId: number,
    limit: number,
    offset: number,
    ordering?: string,
    filterParams?: string
  ) =>
    `/trainings/?product=${productId}&limit=${limit}&offset=${offset}${
      ordering ? `&ordering=${ordering}` : ''
    }${filterParams || ''}`,
  getTrainingsByDataset: (datasetId: number, limit: number, offset: number) =>
    `/trainings/?dataset=${datasetId}&limit=${limit}&offset=${offset}`,
  getTraining: (id: number) => `/trainings/${id}/`,
  getTrainingDownload: (id: number) => `/trainings/${id}/download/`,
  createTraining: '/trainings/',
  abortTraining: (id: number) => `/trainings/${id}/abort/`,
  getDatasetsByTraining: (trainingId: number) =>
    `/dataset/?training=${trainingId}`,
  getDatasetsByTrainingTest: (trainingTestId: number) =>
    `/dataset/?training_test=${trainingTestId}`,
  getTrainingTests: (
    trainingId: number,
    limit: number,
    offset: number,
    ordering?: string,
    filterParams?: string
  ) =>
    `/training_tests/?training=${trainingId}&limit=${limit}&offset=${offset}${
      ordering ? `&ordering=${ordering}` : ''
    }${filterParams || ''}`,
  getTrainingTest: (trainingTestId: number) =>
    `/training_tests/${trainingTestId}/`,
  createTrainingTest: '/training_tests/',
  abortTrainingTest: (trainingTestId: number) =>
    `/training_tests/${trainingTestId}/abort/`,
  createDataset: '/dataset/',
  deleteDataset: (datasetId: number) => `/dataset/${datasetId}/`,
}

const fetchProjects = (
  dispatch: Dispatch<Actions>,
  more: boolean,
  limit: number,
  offset: number,
  ordering?: string,
  filterParams?: string
) => {
  get<Projects>(endpoints.getProjects(limit, offset, ordering, filterParams))
    .then((data) => {
      if (more) {
        dispatch({
          type: Types.FetchMoreProjects,
          payload: data,
        })
      } else {
        dispatch({
          type: Types.FetchProjects,
          payload: data,
        })
      }
    })
    .catch((errors: AxiosError) => {
      handleApiErrors(errors)
    })
}

const emptyProjects = (dispatch: Dispatch<Actions>) =>
  dispatch({
    type: Types.EmptyProjects,
  })

const fetchProject = (dispatch: Dispatch<Actions>, projectId: number) => {
  get<Project>(endpoints.getProject(projectId))
    .then((data) => {
      dispatch({
        type: Types.FetchProject,
        payload: data,
      })
    })
    .catch((errors: AxiosError) => {
      handleApiErrors(errors)
    })
}

const setProject = (dispatch: Dispatch<Actions>, project: Project) => {
  dispatch({
    type: Types.FetchProject,
    payload: project,
  })
}

const createProject = (
  dispatch: Dispatch<Actions>,
  name: string,
  description: string,
  isPrivate: boolean,
  setSubmitting: (isSubmitting: boolean) => void,
  setModal: (value: boolean) => void
) => {
  post<Project>(endpoints.createProject, {
    name,
    description,
    private: isPrivate,
  })
    .then((data) => {
      setSubmitting(false)
      setModal(false)
      toast.success(i18n.t('The new project has been created'))

      dispatch({
        type: Types.CreateProject,
        payload: data,
      })
    })
    .catch((errors: AxiosError) => {
      setSubmitting(false)
      handleApiErrors(errors)
    })
}

const updateProject = (
  dispatch: Dispatch<Actions>,
  projectId: number,
  name: string,
  description: string,
  isPrivate: boolean,
  setSubmitting: (isSubmitting: boolean) => void,
  setModal: (value: boolean) => void
) => {
  update<Project>(endpoints.updateProject(projectId), {
    name,
    description,
    private: isPrivate,
  })
    .then((data) => {
      setSubmitting(false)
      setModal(false)
      toast.success(i18n.t('The project has been edited'))

      dispatch({
        type: Types.UpdateProject,
        payload: data,
      })
    })
    .catch((errors: AxiosError) => {
      setSubmitting(false)
      handleApiErrors(errors)
    })
}

const deleteProject = (
  dispatch: Dispatch<Actions>,
  projectId: number,
  setSubmitting: (isSubmitting: boolean) => void
) => {
  setSubmitting(true)
  del(endpoints.getProject(projectId))
    .then(() => {
      setSubmitting(false)
      dispatch({
        type: Types.DeleteProject,
        payload: projectId,
      })
    })
    .catch((errors: AxiosError) => {
      setSubmitting(false)
      handleApiErrors(errors)
    })
}

const fetchProducts = (
  dispatch: Dispatch<Actions>,
  more: boolean,
  projectId: number,
  limit: number,
  offset: number,
  ordering?: string,
  filterParams?: string
) => {
  get<Products>(
    endpoints.getProducts(projectId, limit, offset, ordering, filterParams)
  )
    .then((data) => {
      if (more) {
        dispatch({
          type: Types.FetchMoreProducts,
          payload: data,
        })
      } else {
        dispatch({
          type: Types.FetchProducts,
          payload: data,
        })
      }
    })
    .catch((errors: AxiosError) => {
      handleApiErrors(errors)
    })
}

const emptyProducts = (dispatch: Dispatch<Actions>) =>
  dispatch({
    type: Types.EmptyProducts,
  })

const fetchProduct = (dispatch: Dispatch<Actions>, productId: number) => {
  get<Product>(endpoints.getProduct(productId))
    .then((data) => {
      dispatch({
        type: Types.SetProduct,
        payload: data,
      })
    })
    .catch((errors: AxiosError) => {
      handleApiErrors(errors)
    })
}

const setProduct = (dispatch: Dispatch<Actions>, product: Product) => {
  dispatch({
    type: Types.SetProduct,
    payload: product,
  })
}

const createProduct = (
  dispatch: Dispatch<Actions>,
  projectId: number,
  name: string,
  description: string,
  type: ProductType,
  classes: string,
  setSubmitting: (isSubmitting: boolean) => void,
  setModal: (value: boolean) => void
) => {
  post<Product>(endpoints.createProduct, {
    project: projectId,
    name,
    description,
    type,
    productClasses: classes,
  })
    .then((data) => {
      setSubmitting(false)
      setModal(false)
      toast.success(i18n.t('The new product has been created'))

      dispatch({
        type: Types.CreateProduct,
        payload: data,
      })
    })
    .catch((errors: AxiosError) => {
      setSubmitting(false)
      handleApiErrors(errors)
    })
}

const updateProduct = (
  dispatch: Dispatch<Actions>,
  productId: number,
  name: string,
  description: string,
  setSubmitting: (isSubmitting: boolean) => void,
  setModal: (value: boolean) => void,
  setLastDescriptionUpdated?: React.Dispatch<
    React.SetStateAction<{
      productId: number
      description: string
    }>
  >
) => {
  update<Product>(endpoints.updateProduct(productId), { name, description })
    .then((data) => {
      setSubmitting(false)
      setModal(false)
      toast.success(i18n.t('The product has been edited'))
      if (setLastDescriptionUpdated)
        setLastDescriptionUpdated({ productId, description })
      dispatch({
        type: Types.UpdateProduct,
        payload: data,
      })
    })
    .catch((errors: AxiosError) => {
      setSubmitting(false)
      handleApiErrors(errors)
    })
}

const deleteProduct = (
  dispatch: Dispatch<Actions>,
  productId: number,
  setSubmitting: (isSubmitting: boolean) => void
) => {
  setSubmitting(true)
  del(endpoints.getProduct(productId))
    .then(() => {
      setSubmitting(false)
      dispatch({
        type: Types.DeleteProduct,
        payload: productId,
      })
    })
    .catch((errors: AxiosError) => {
      setSubmitting(false)
      handleApiErrors(errors)
    })
}

const fetchProductClasses = (
  dispatch: Dispatch<Actions>,
  productId: number,
  limit: number,
  offset: number
) => {
  get<ProductClasses>(endpoints.getProductClasses(productId, limit, offset))
    .then((data) => {
      dispatch({
        type: Types.FetchProductClasses,
        payload: data,
      })
    })
    .catch((errors: AxiosError) => {
      handleApiErrors(errors)
    })
}

const emptyProductClasses = (dispatch: Dispatch<Actions>) =>
  dispatch({
    type: Types.EmptyProductClasses,
  })

const fetchTrainings = (
  dispatch: Dispatch<Actions>,
  more: boolean,
  productId: number,
  limit: number,
  offset: number,
  ordering?: string,
  filterParams?: string,
  signal?: AbortSignal
) =>
  get<Trainings>(
    endpoints.getTrainings(productId, limit, offset, ordering, filterParams),
    signal ? { signal } : undefined
  )
    .then((data) => {
      if (more) {
        dispatch({
          type: Types.FetchMoreTrainings,
          payload: data,
        })
      } else {
        dispatch({
          type: Types.FetchTrainings,
          payload: data,
        })
      }
    })
    .catch((errors: AxiosError) => {
      if (axios.isCancel(errors)) return
      handleApiErrors(errors)
    })

const emptyTrainings = (dispatch: Dispatch<Actions>) =>
  dispatch({
    type: Types.EmptyTrainings,
  })

const fetchTraining = (dispatch: Dispatch<Actions>, projectId: number) => {
  get<Training>(endpoints.getTraining(projectId))
    .then((data) => {
      dispatch({
        type: Types.SetTraining,
        payload: data,
      })
    })
    .catch((errors: AxiosError) => {
      handleApiErrors(errors)
    })
}

export interface DatasetUsedInTraining {
  id: number
  name: string
  updateDate: string
}

interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

const fetchDatasetsUsedInTraining = async (
  trainingId: number
): Promise<DatasetUsedInTraining[]> => {
  const response = await get<PaginatedResponse<DatasetUsedInTraining>>(
    endpoints.getDatasetsByTraining(trainingId)
  )
  return response.results
}

const fetchDatasetsUsedInTrainingTest = async (
  trainingTestId: number
): Promise<DatasetUsedInTraining[]> => {
  const response = await get<PaginatedResponse<DatasetUsedInTraining>>(
    endpoints.getDatasetsByTrainingTest(trainingTestId)
  )
  return response.results
}

const setTraining = (dispatch: Dispatch<Actions>, training: Training) => {
  dispatch({
    type: Types.SetTraining,
    payload: training,
  })
}

export type Metadata = {
  horizontalPatches?: number
  verticalPatches?: number
  patchesOverlap?: number
  patchesStrategyId?: number
  patchesClassPriority?: string[]
  anomalyStrategyId?: number
  inputHeight?: number
  inputWidth?: number
  precision?: string
}

const createTraining = (
  projectId: number,
  productId: number,
  backboneId: number,
  observationsIds: number[],
  setSubmitting: (isSubmitting: boolean) => void,
  push: History['push'],
  metadata?: Metadata,
  name?: string,
  notes?: string,
  startTest?: boolean,
  datasetsIds?: number[]
) => {
  post<Training>(endpoints.createTraining, {
    productId,
    observationsIds,
    backboneId,
    metadata,
    name,
    notes,
    datasets_used_in_training: datasetsIds,
  })
    .then((dataTraining) => {
      if (startTest) {
        const trainingId = dataTraining.id
        const testName = `automatic-test`
        startTestAfterTraining(productId, trainingId, observationsIds, testName)
      }
      setSubmitting(false)
      push(locations.trainings(projectId, productId))
      toast.success(i18n.t('The training process has been added to the queue'))
    })
    .catch((errors: AxiosError) => {
      setSubmitting(false)
      handleApiErrors(errors)
    })
}

const startTestAfterTraining = (
  productId: number,
  trainingId: number,
  observationsTrainingIds: number[],
  name?: string
) => {
  get<ObservationsResult>(
    `/observations/?${getQueryString({
      product: productId,
      offset: 0,
      limit: 20000, // limit is required
      mode: 'no_image_data', // lightweight version of the response payload
    })}`
  ).then((dataObs): void => {
    const testObservationsIds = dataObs.results
      .filter((o) => !observationsTrainingIds.includes(o.id))
      .map((o) => o.id)
    post<TrainingTest>(endpoints.createTrainingTest, {
      trainingId,
      observationsIds: testObservationsIds,
      name,
    })
  })
}

const deleteTraining = (
  dispatch: Dispatch<Actions>,
  trainingId: number,
  setSubmitting: (isSubmitting: boolean) => void
) => {
  setSubmitting(true)
  del(endpoints.getTraining(trainingId))
    .then(() => {
      setSubmitting(false)
      dispatch({
        type: Types.DeleteTraining,
        payload: trainingId,
      })
    })
    .catch((errors: AxiosError) => {
      setSubmitting(false)
      handleApiErrors(errors)
    })
}

const updateRunStatus = (dispatch: Dispatch<Actions>, data: RunStatus) => {
  switch (data.eventType) {
    case 'TRAINING_UPDATE':
      return dispatch({
        type: Types.UpdateTrainingStatus,
        payload: data,
      })

    case 'TRAINING_TEST_UPDATE':
      return dispatch({
        type: Types.UpdateTrainingTestStatus,
        payload: data,
      })

    default:
      return false
  }
}

const abortTraining = (
  dispatch: Dispatch<Actions>,
  trainingId: number,
  setSubmitting: (isSubmitting: boolean) => void
) => {
  post(endpoints.abortTraining(trainingId), {})
    .then(() => {
      setSubmitting(false)
      toast.success(i18n.t('The training process has been aborted'))
    })
    .catch((errors: AxiosError) => {
      setSubmitting(false)
      handleApiErrors(errors)
    })
}

const downloadTrainingFile = (
  dispatch: Dispatch<Actions>,
  trainingId: number
) => {
  get<TrainingDownload>(endpoints.getTrainingDownload(trainingId))
    .then((data) => {
      if (data.outputFile) {
        window.open(data.outputFile)
      }
    })
    .catch((errors: AxiosError) => {
      handleApiErrors(errors)
    })
}

const fetchTrainingTests = (
  dispatch: Dispatch<Actions>,
  more: boolean,
  trainingId: number,
  limit: number,
  offset: number,
  ordering?: string,
  filterParams?: string,
  signal?: AbortSignal
) =>
  get<TrainingTests>(
    endpoints.getTrainingTests(
      trainingId,
      limit,
      offset,
      ordering,
      filterParams
    ),
    signal ? { signal } : undefined
  )
    .then((data) => {
      if (more) {
        dispatch({
          type: Types.FetchMoreTrainingTests,
          payload: data,
        })
      } else {
        dispatch({
          type: Types.FetchTrainingTests,
          payload: data,
        })
      }
    })
    .catch((errors: AxiosError) => {
      if (axios.isCancel(errors)) return
      handleApiErrors(errors)
    })

const emptyTrainingTests = (dispatch: Dispatch<Actions>) =>
  dispatch({
    type: Types.EmptyTrainingTests,
  })

const fetchTrainingTest = (
  dispatch: Dispatch<Actions>,
  trainingTestId: number
) => {
  get<TrainingTest>(endpoints.getTrainingTest(trainingTestId))
    .then((data) => {
      dispatch({
        type: Types.SetTrainingTest,
        payload: data,
      })
    })
    .catch((errors: AxiosError) => {
      handleApiErrors(errors)
    })
}

const setTrainingTest = (
  dispatch: Dispatch<Actions>,
  trainingTest: TrainingTest
) => {
  dispatch({
    type: Types.SetTrainingTest,
    payload: trainingTest,
  })
}

const deleteTrainingTest = (
  dispatch: Dispatch<Actions>,
  trainingTestId: number,
  setSubmitting: (isSubmitting: boolean) => void
) => {
  setSubmitting(true)
  del(endpoints.getTrainingTest(trainingTestId))
    .then(() => {
      setSubmitting(false)
      dispatch({
        type: Types.DeleteTrainingTest,
        payload: trainingTestId,
      })
    })
    .catch((errors: AxiosError) => {
      setSubmitting(false)
      handleApiErrors(errors)
    })
}

const abortTrainingTest = (
  dispatch: Dispatch<Actions>,
  trainingTestId: number,
  setSubmitting: (isSubmitting: boolean) => void
) => {
  post(endpoints.abortTrainingTest(trainingTestId), {})
    .then(() => {
      setSubmitting(false)
      toast.success(i18n.t('The test process has been aborted'))
    })
    .catch((errors: AxiosError) => {
      setSubmitting(false)
      handleApiErrors(errors)
    })
}

const createTrainingTest = (
  projectId: number,
  productId: number,
  trainingId: number,
  observationsIds: number[],
  setSubmitting: (isSubmitting: boolean) => void,
  push: History['push'],
  name?: string,
  notes?: string,
  startTest?: boolean,
  datasetsIds?: number[]
) => {
  post<TrainingTest>(endpoints.createTrainingTest, {
    trainingId,
    observationsIds,
    name,
    notes,
    datasets_used_in_test: datasetsIds,
  })
    .then(() => {
      setSubmitting(false)
      if (!startTest) {
        push(locations.trainings(projectId, productId, trainingId), {
          trainingId,
        })
      } else {
        push(locations.trainings(projectId, productId))
      }
      toast.success(i18n.t('The test process has been added to the queue'))
    })
    .catch((errors: AxiosError) => {
      setSubmitting(false)
      handleApiErrors(errors)
    })
}

const showAlertNotification = (
  dispatch: Dispatch<Actions>,
  data: AlertNotification
) => {
  if (data.eventType === 'IMAGE_UPLOAD_UPDATE') {
    dispatch({
      type: Types.AlertNotificationImageUpload,
      payload: data,
    })
    return true
  }

  return false
}

export {
  // Projects
  fetchProjects,
  emptyProjects,
  fetchProject,
  setProject,
  createProject,
  updateProject,
  deleteProject,
  // Products
  fetchProducts,
  emptyProducts,
  fetchProduct,
  setProduct,
  createProduct,
  updateProduct,
  deleteProduct,
  // ProductClasses
  fetchProductClasses,
  emptyProductClasses,
  // Trainings
  fetchTrainings,
  emptyTrainings,
  fetchTraining,
  setTraining,
  createTraining,
  deleteTraining,
  updateRunStatus,
  abortTraining,
  downloadTrainingFile,
  // Tests
  fetchTrainingTests,
  emptyTrainingTests,
  fetchTrainingTest,
  setTrainingTest,
  deleteTrainingTest,
  abortTrainingTest,
  createTrainingTest,
  // AlertNotification
  showAlertNotification,
  // Datasets
  fetchDatasetsUsedInTraining,
  fetchDatasetsUsedInTrainingTest,
  endpoints,
}
